/**
 * @fileoverview Registry of builder help illustrations keyed by IllustrationId (Phase 5, J3).
 * @module ui-admin/help/illustrations
 *
 * Responsibilities: - Map each `IllustrationId` to its SVG React component so the help panel can
 *                     resolve a content section\u2019s `illustration` id to a renderable component.
 * Dependencies:     - The individual `Illustration*` components; `IllustrationId` from `../types`.
 * Architecture:     - Pure lookup. No state, no data, no side effects.
 * Notes:            - The `satisfies Record<IllustrationId, ...>` assertion guarantees every id has
 *                     a component (and no stray ids exist) at compile time.
 */

import type React from 'react';
import type { IllustrationId } from '../types';
import { IllustrationBuilderShell } from './IllustrationBuilderShell';
import { IllustrationBandCard } from './IllustrationBandCard';
import { IllustrationDynamicBand } from './IllustrationDynamicBand';
import { IllustrationCoverPageTab } from './IllustrationCoverPageTab';
import { IllustrationMergeField } from './IllustrationMergeField';
import { IllustrationPreviewPane } from './IllustrationPreviewPane';

/**
 * @contract ILLUSTRATIONS
 * Purpose: Resolve an `IllustrationId` to its SVG component for the help panel.
 * Rules: Exactly one component per `IllustrationId` (enforced by `satisfies`).
 */
export const ILLUSTRATIONS = {
  builderShell: IllustrationBuilderShell,
  bandCard: IllustrationBandCard,
  dynamicBand: IllustrationDynamicBand,
  coverPageTab: IllustrationCoverPageTab,
  mergeField: IllustrationMergeField,
  previewPane: IllustrationPreviewPane,
} satisfies Record<IllustrationId, React.FC>;

export {
  IllustrationBuilderShell,
  IllustrationBandCard,
  IllustrationDynamicBand,
  IllustrationCoverPageTab,
  IllustrationMergeField,
  IllustrationPreviewPane,
};
